import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "timeline", "stepForm", "stepFormTitle", "editingId",
    "seqSubject", "seqBody", "seqBodyEditor", "seqDelay", "seqCondition",
    "previewPanel", "previewSubject", "previewBody", "leadListSelect",
    "agentShell", "agentRail", "readinessSummary", "sequenceReadinessItem",
    "sequenceReadinessDetail"
  ]

  static values = {
    campaignId: Number,
    sequences: Array,
    status: String,
    msgConfirmDelete: { type: String, default: "삭제하시겠습니까?" },
    msgModalTitleAdd: { type: String, default: "단계 추가" },
    msgSaveError: { type: String, default: "저장에 실패했습니다." },
    msgConfirmDeleteStep: { type: String, default: "이 단계를 삭제하시겠습니까?" },
    msgAlertNoFirstStep: { type: String, default: "먼저 첫 번째 단계를 추가하세요." },
    msgAiGenerateSuccess: { type: String, default: "%{count}개의 후속 단계가 생성되었습니다." },
    msgAiGenerateParseError: { type: String, default: "AI 응답을 파싱하지 못했습니다. 직접 추가해주세요." },
    msgAiGenerateError: { type: String, default: "AI 생성에 실패했습니다." },
    msgAiSequenceError: { type: String, default: "AI 시퀀스 생성에 실패했습니다." },
    msgModalTitleEdit: { type: String, default: "단계 %{step} 편집" },
    readinessSummaryTemplate: { type: String, default: "%{total}개 중 %{ready}개 준비 완료" },
    sequenceReadinessDetailTemplate: { type: String, default: "%{count}개 단계 구성됨" }
  }

  connect() {
    this.loadSequencesFromJson()
    this.handleStepActionClick = this.handleStepActionClick.bind(this)
    this.syncAgentRailState = this.syncAgentRailState.bind(this)
    this.handleAiSequencesCreated = this.handleAiSequencesCreated.bind(this)
    this.handleAiSequencesRemoved = this.handleAiSequencesRemoved.bind(this)
    this.element.addEventListener("click", this.handleStepActionClick)
    window.addEventListener("coldmail:sequences-created", this.handleAiSequencesCreated)
    window.addEventListener("coldmail:sequences-removed", this.handleAiSequencesRemoved)
    this.observeAgentRail()
    this.updateSequenceReadiness()
    requestAnimationFrame(this.syncAgentRailState)
  }

  disconnect() {
    this.element.removeEventListener("click", this.handleStepActionClick)
    window.removeEventListener("coldmail:sequences-created", this.handleAiSequencesCreated)
    window.removeEventListener("coldmail:sequences-removed", this.handleAiSequencesRemoved)
    this.agentRailObserver?.disconnect()
    window.removeEventListener("resize", this.syncAgentRailState)
  }

  handleStepActionClick(event) {
    const trigger = event.target.closest("[data-cmk-step-action]")
    if (!trigger || !this.element.contains(trigger)) return

    event.preventDefault()
    event.stopPropagation()

    const action = trigger.dataset.cmkStepAction
    if (action === "add") {
      this.openAddStep()
    } else if (action === "edit") {
      this.openEditStep(trigger.dataset.sequenceId)
    } else if (action === "delete") {
      this.deleteStepById(trigger.dataset.sequenceId)
    }
  }

  loadSequencesFromJson() {
    const source = document.getElementById("coldmail-campaign-sequences-json")
    if (!source) return

    try {
      const sequences = JSON.parse(source.textContent || "[]")
      this.sequencesValue = Array.isArray(sequences) ? sequences : []
    } catch (error) {
      console.error("Coldmail sequence data could not be parsed:", error)
      this.sequencesValue = []
    }
  }

  _interpolate(template, vars) {
    return template.replace(/%\{(\w+)\}/g, (_, k) => vars[k] !== undefined ? vars[k] : "")
  }

  get csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.getAttribute("content") : ""
  }

  target(name) {
    return document.querySelector(`[data-coldmail-campaign-target~="${name}"]`)
  }

  setBodyEditor(value) {
    const html = value || ""
    const hidden = this.target("seqBody")
    if (!hidden) return

    hidden.value = html
    hidden.dispatchEvent(new CustomEvent("toast-editor:set-value", {
      bubbles: true,
      detail: { value: html }
    }))
  }

  bodyEditorHtml() {
    const hidden = this.target("seqBody")
    hidden?.dispatchEvent(new CustomEvent("toast-editor:sync", { bubbles: true }))
    return (hidden?.value || "").trim()
  }

  generatedEmailBodyHtml(value) {
    const raw = String(value || "").trim()
    if (!raw) return ""

    const withoutArtifacts = raw
      .replace(/alt=(['"])Cold mail image\1/gi, 'alt=""')
      .replace(/<figcaption[^>]*>\s*Cold mail image\s*<\/figcaption>/gi, "")
      .replace(/<p[^>]*>\s*Cold mail image\s*<\/p>/gi, "")
      .replace(/^\s*Cold mail image\s*$/gim, "")
      .trim()

    if (/<(p|br|div|ul|ol|li|blockquote|table|h[1-6]|figure|img)\b/i.test(withoutArtifacts)) return withoutArtifacts

    return withoutArtifacts
      .replace(/\r\n?/g, "\n")
      .split(/\n+/)
      .map(line => line.trim())
      .filter(Boolean)
      .map(line => `<p>${this.escapeHtml(line)}</p>`)
      .join("\n")
  }

  escapeHtml(value) {
    return String(value || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/\"/g, "&quot;")
      .replace(/'/g, "&#39;")
  }

  observeAgentRail() {
    const rail = this.target("agentRail")
    if (!rail || typeof MutationObserver === "undefined") return

    this.agentRailObserver = new MutationObserver(this.syncAgentRailState)
    this.agentRailObserver.observe(rail, {
      attributes: true,
      childList: true,
      subtree: true,
      attributeFilter: ["class", "style", "hidden", "aria-expanded"]
    })
    window.addEventListener("resize", this.syncAgentRailState)
  }

  syncAgentRailState() {
    const shell = this.target("agentShell")
    const rail = this.target("agentRail")
    if (!shell || !rail) return

    const wrapper = rail.querySelector("#ka-wrapper")
    const isClosed = this.agentRailIsClosed(wrapper)
    shell.classList.toggle("cmk-shell--agent-collapsed", isClosed)
    rail.classList.toggle("is-collapsed", isClosed)
  }

  agentRailIsClosed(wrapper) {
    if (!wrapper) return false
    if (wrapper.classList.contains("ka-closed")) return true
    if (wrapper.style.width === "0px" || wrapper.style.width === "0") return true

    const rect = wrapper.getBoundingClientRect()
    return rect.width > 0 && rect.width <= 4
  }

  handleAiSequencesCreated(event) {
    const sequences = Array.isArray(event.detail?.sequences) ? event.detail.sequences : []
    if (sequences.length === 0) return

    const existingIds = new Set(this.sequencesValue.map((sequence) => String(sequence.id)))
    const newSequences = sequences.filter((sequence) => sequence?.id && !existingIds.has(String(sequence.id)))
    if (newSequences.length === 0) return

    this.sequencesValue = [...this.sequencesValue, ...newSequences]
    newSequences.forEach((sequence) => this.appendSequenceCard(sequence))
    this.updateSequenceReadiness()
  }

  handleAiSequencesRemoved(event) {
    const ids = new Set((event.detail?.ids || []).map((id) => String(id)))
    if (ids.size === 0) return

    this.sequencesValue = this.sequencesValue.filter((sequence) => !ids.has(String(sequence.id)))
    ids.forEach((id) => this.target("timeline")?.querySelector(`[data-sequence-id="${id}"]`)?.remove())
    this.updateSequenceReadiness()
  }

  updateSequenceReadiness() {
    const sequenceReady = this.sequencesValue.some((sequence) => {
      return String(sequence?.subject || "").trim() && String(sequence?.body || "").trim()
    })
    const sequenceCount = this.sequencesValue.length

    this.sequenceReadinessItemTargets.forEach((item) => {
      item.dataset.cmkReadinessReady = sequenceReady ? "true" : "false"
      item.classList.toggle("is-ready", sequenceReady)
    })

    this.sequenceReadinessDetailTargets.forEach((detail) => {
      detail.textContent = this._interpolate(this.sequenceReadinessDetailTemplateValue, { count: sequenceCount })
    })

    if (!this.hasReadinessSummaryTarget) return

    const items = this.element.querySelectorAll("[data-cmk-readiness-ready]")
    const readyCount = Array.from(items).filter((item) => item.dataset.cmkReadinessReady === "true").length
    this.readinessSummaryTarget.textContent = this._interpolate(this.readinessSummaryTemplateValue, {
      ready: readyCount,
      total: items.length
    })
  }

  appendSequenceCard(sequence) {
    const timeline = this.target("timeline")
    if (!timeline || !sequence?.id) return

    timeline.insertAdjacentHTML("beforeend", this.sequenceCardHtml(sequence))
    const card = timeline.querySelector(`[data-sequence-id="${sequence.id}"]`)
    card?.scrollIntoView({ behavior: "smooth", block: "nearest" })
  }

  sequenceCardHtml(sequence) {
    const conditionLabel = this.sequenceConditionLabel(sequence.condition)
    return `
      <article class="cmk-step" data-sequence-id="${this.escapeHtml(sequence.id)}">
        <div class="cmk-step__number">${this.escapeHtml(sequence.step_number || "")}</div>
        <div>
          <div class="cmk-step__header">
            <div class="cmk-step__title">${this.escapeHtml(sequence.subject || "(제목 없음)")}</div>
            <div class="cmk-step__actions">
              <button type="button" class="cmk-step__action" data-cmk-step-action="edit" data-sequence-id="${this.escapeHtml(sequence.id)}">편집</button>
              <button type="button" class="cmk-step__action cmk-step__action--delete" data-cmk-step-action="delete" data-sequence-id="${this.escapeHtml(sequence.id)}">삭제</button>
            </div>
          </div>
          <div class="cmk-step__body">${this.sanitizeEmailHtml(sequence.body || "")}</div>
          <div class="cmk-step__meta">
            <span>대기 ${this.escapeHtml(sequence.delay_days ?? 0)}일</span>
            <span>${this.escapeHtml(conditionLabel)}</span>
            <span>발송 0</span>
          </div>
        </div>
      </article>`
  }

  sequenceConditionLabel(condition) {
    if (condition === "no_reply") return "미회신 시"
    if (condition === "no_open") return "미오픈 시"
    return "모두"
  }

  sanitizeEmailHtml(value) {
    const template = document.createElement("template")
    template.innerHTML = String(value || "")
    template.content.querySelectorAll("script, style, iframe, object, embed").forEach((node) => node.remove())
    template.content.querySelectorAll("*").forEach((node) => {
      Array.from(node.attributes || []).forEach((attr) => {
        if (/^on/i.test(attr.name)) node.removeAttribute(attr.name)
        if (["href", "src"].includes(attr.name) && /^javascript:/i.test(attr.value.trim())) node.removeAttribute(attr.name)
      })
    })
    return template.innerHTML
  }

  showStepForm() {
    const form = this.target("stepForm")
    if (!form) return

    form.classList.add("active")
    form.scrollIntoView({ behavior: "smooth", block: "nearest" })
    this.target("seqSubject")?.focus()
  }

  hideStepForm() {
    this.target("stepForm")?.classList.remove("active")
  }

  // -- Start Campaign --
  async start() {
    try {
      const res = await fetch(`/coldmail/campaigns/${this.campaignIdValue}/start`, {
        method: "POST",
        headers: { "X-CSRF-Token": this.csrfToken, "Content-Type": "application/json" }
      })
      const data = await res.json()
      if (data.success) {
        window.location.reload()
      } else {
        alert((data.errors || []).join("\n") || "발송을 시작할 수 없습니다.")
      }
    } catch (e) {
      console.error("Start failed:", e)
    }
  }

  // -- Pause Campaign --
  async pause() {
    try {
      const res = await fetch(`/coldmail/campaigns/${this.campaignIdValue}/pause`, {
        method: "POST",
        headers: { "X-CSRF-Token": this.csrfToken, "Content-Type": "application/json" }
      })
      const data = await res.json()
      if (data.success) {
        window.location.reload()
      }
    } catch (e) {
      console.error("Pause failed:", e)
    }
  }

  async saveLeadList(event) {
    event?.preventDefault()
    const select = this.target("leadListSelect")
    const leadListId = select?.value
    if (!leadListId) {
      alert("대상 리스트를 선택해주세요.")
      return
    }

    try {
      const res = await fetch(`/coldmail/campaigns/${this.campaignIdValue}`, {
        method: "PATCH",
        headers: {
          "X-CSRF-Token": this.csrfToken,
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify({ campaign: { coldmail_lead_list_id: leadListId } })
      })
      const data = await res.json()
      if (data.success) {
        window.location.reload()
      } else {
        alert((data.errors || []).join("\n") || this.msgSaveErrorValue)
      }
    } catch (e) {
      console.error("Lead list save failed:", e)
      alert(this.msgSaveErrorValue)
    }
  }

  // -- Destroy Campaign --
  async destroy(event) {
    const msg = event.currentTarget.dataset.confirm || this.msgConfirmDeleteValue
    if (!confirm(msg)) return
    try {
      const res = await fetch(`/coldmail/campaigns/${this.campaignIdValue}`, {
        method: "DELETE",
        headers: { "X-CSRF-Token": this.csrfToken, "Content-Type": "application/json" }
      })
      if (res.redirected) {
        window.location.href = res.url
      } else {
        window.location.href = "/coldmail/campaigns"
      }
    } catch (e) {
      console.error("Delete failed:", e)
    }
  }

  // -- Preview --
  async preview() {
    try {
      const res = await fetch(`/coldmail/campaigns/${this.campaignIdValue}/preview`, {
        headers: { "X-CSRF-Token": this.csrfToken, "Accept": "application/json" }
      })
      const data = await res.json()
      this.target("previewSubject").textContent = data.subject || "(제목 없음)"
      this.target("previewBody").textContent = data.body || "(본문 없음)"
      const panel = this.target("previewPanel")
      panel?.classList.add("active")
      panel?.scrollIntoView({ behavior: "smooth", block: "nearest" })
    } catch (e) {
      console.error("Preview failed:", e)
    }
  }

  closePreview(event) {
    event?.preventDefault()
    event?.stopPropagation()
    this.target("previewPanel")?.classList.remove("active")
  }

  // -- Add Step --
  openAddStep() {
    this.target("editingId").value = ""
    this.target("stepFormTitle").textContent = this.msgModalTitleAddValue
    this.target("seqSubject").value = ""
    this.setBodyEditor("")
    this.target("seqDelay").value = "3"
    this.target("seqCondition").value = "no_reply"
    this.showStepForm()
  }

  // -- Edit Step --
  editStep(event) {
    this.openEditStep(event.currentTarget.dataset.sequenceId)
  }

  openEditStep(id) {
    const seq = this.sequencesValue.find(s => String(s.id) === String(id))
    if (!seq) return

    this.target("editingId").value = id
    this.target("stepFormTitle").textContent = this._interpolate(this.msgModalTitleEditValue, { step: seq.step_number })
    this.target("seqSubject").value = seq.subject || ""
    this.setBodyEditor(seq.body || "")
    this.target("seqDelay").value = seq.delay_days || 3
    this.target("seqCondition").value = seq.condition || "no_reply"
    this.showStepForm()
  }

  cancelStepForm(event) {
    event?.preventDefault()
    event?.stopPropagation()
    this.target("editingId").value = ""
    this.hideStepForm()
  }

  // -- Save Step (create or update) --
  async saveStep() {
    const editingId = this.target("editingId").value
    const payload = {
      sequence: {
        subject: this.target("seqSubject").value,
        body: this.bodyEditorHtml(),
        delay_days: parseInt(this.target("seqDelay").value) || 3,
        condition: this.target("seqCondition").value
      }
    }

    try {
      let url, method
      if (editingId) {
        url = `/coldmail/campaigns/${this.campaignIdValue}/sequences/${editingId}`
        method = "PATCH"
      } else {
        url = `/coldmail/campaigns/${this.campaignIdValue}/sequences`
        method = "POST"
      }

      const res = await fetch(url, {
        method,
        headers: {
          "X-CSRF-Token": this.csrfToken,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(payload)
      })
      const data = await res.json()
      if (data.success) {
        window.location.reload()
      } else {
        alert((data.errors || []).join("\n") || this.msgSaveErrorValue)
      }
    } catch (e) {
      console.error("Save step failed:", e)
    }
  }

  // -- AI Generate single step --
  async aiGenerate() {
    const subject = this.target("seqSubject").value
    const prompt = subject
      ? `다음 제목의 홍보 메일 본문을 작성해줘. 제목: "${subject}". {{name}}, {{company}} 등 변수를 활용해서 개인화된 짧은 홍보 메일을 작성해. 스팸처럼 보이지 않게, 간결하고 자연스러운 톤으로. 본문만 반환해.`
      : "잠재고객에게 보내는 첫 홍보 메일을 작성해줘. {{name}}, {{company}} 등 변수를 활용하고, 짧고 개인화된 톤으로. 제목과 본문을 모두 작성해. 형식: 제목: ...\n본문: ..."

    try {
      const res = await fetch("/coldmail/ai/claude", {
        method: "POST",
        headers: { "X-CSRF-Token": this.csrfToken, "Content-Type": "application/json" },
        body: JSON.stringify({
          system: "당신은 홍보 메일 전문가입니다. 짧고 개인화된 아웃바운드 이메일을 작성하세요. 스팸처럼 보이지 않게 자연스럽고 간결하게 작성하세요. {{name}}, {{company}}, {{position}}, {{industry}} 변수를 적절히 활용하세요.",
          messages: [{ role: "user", content: prompt }]
        })
      })
      const data = await res.json()
      const text = data.content?.[0]?.text || data.text || ""
      if (text) {
        if (!subject && text.includes("제목:")) {
          const lines = text.split("\n")
          const subjectLine = lines.find(l => l.startsWith("제목:"))
          if (subjectLine) this.target("seqSubject").value = subjectLine.replace("제목:", "").trim()
          const bodyStart = lines.findIndex(l => l.startsWith("본문:"))
          if (bodyStart >= 0) {
            this.setBodyEditor(this.generatedEmailBodyHtml(lines.slice(bodyStart).join("\n").replace("본문:", "").trim()))
          } else {
            this.setBodyEditor(this.generatedEmailBodyHtml(text))
          }
        } else {
          this.setBodyEditor(this.generatedEmailBodyHtml(text))
        }
      }
    } catch (e) {
      console.error("AI generate failed:", e)
      alert(this.msgAiGenerateErrorValue)
    }
  }

  // -- AI Generate full sequence (steps 2, 3) --
  async aiGenerateSequence() {
    const existingSteps = this.sequencesValue
    if (existingSteps.length === 0) {
      alert(this.msgAlertNoFirstStepValue)
      return
    }

    const step1 = existingSteps[0]
    const prompt = `다음은 첫 번째 홍보 메일입니다:\n제목: ${step1.subject}\n본문: ${step1.body}\n\n이 첫 메일에 회신이 없을 경우 보낼 후속 시퀀스 2단계를 작성해줘.\nStep 2 (3일 후, 미회신 시): 첫 메일을 참조하는 가벼운 후속\nStep 3 (5일 후, 미회신 시): 마지막 연락\n\n각 단계별로 다음 형식으로:\n---\nStep N\n제목: ...\n본문: ...\n---`

    try {
      const res = await fetch("/coldmail/ai/claude", {
        method: "POST",
        headers: { "X-CSRF-Token": this.csrfToken, "Content-Type": "application/json" },
        body: JSON.stringify({
          system: "당신은 홍보 메일 시퀀스 전문가입니다. 각 후속 메일은 이전 메일을 자연스럽게 참조하되, 스팸처럼 보이지 않아야 합니다. {{name}}, {{company}} 변수를 활용하세요.",
          messages: [{ role: "user", content: prompt }]
        })
      })
      const data = await res.json()
      const text = data.content?.[0]?.text || data.text || ""

      // Parse AI response and create steps
      const steps = text.split("---").filter(s => s.trim())
      let created = 0
      for (const stepText of steps) {
        const lines = stepText.trim().split("\n")
        const subjectLine = lines.find(l => l.startsWith("제목:"))
        const bodyStartIdx = lines.findIndex(l => l.startsWith("본문:"))
        if (!subjectLine) continue

        const subject = subjectLine.replace("제목:", "").trim()
        const body = bodyStartIdx >= 0 ? this.generatedEmailBodyHtml(lines.slice(bodyStartIdx).join("\n").replace("본문:", "").trim()) : ""
        const delayDays = created === 0 ? 3 : 5

        const createRes = await fetch(`/coldmail/campaigns/${this.campaignIdValue}/sequences`, {
          method: "POST",
          headers: { "X-CSRF-Token": this.csrfToken, "Content-Type": "application/json" },
          body: JSON.stringify({ sequence: { subject, body, delay_days: delayDays, condition: "no_reply", ai_generated: true } })
        })
        const createData = await createRes.json()
        if (createData.success) created++
      }

      if (created > 0) {
        alert(this._interpolate(this.msgAiGenerateSuccessValue, { count: created }))
        window.location.reload()
      } else {
        alert(this.msgAiGenerateParseErrorValue)
      }
    } catch (e) {
      console.error("AI sequence generate failed:", e)
      alert(this.msgAiSequenceErrorValue)
    }
  }

  // -- Delete Step --
  async deleteStep(event) {
    this.deleteStepById(event.currentTarget.dataset.sequenceId)
  }

  async deleteStepById(id) {
    if (!confirm(this.msgConfirmDeleteStepValue)) return

    try {
      const res = await fetch(`/coldmail/campaigns/${this.campaignIdValue}/sequences/${id}`, {
        method: "DELETE",
        headers: { "X-CSRF-Token": this.csrfToken, "Content-Type": "application/json" }
      })
      const data = await res.json().catch(() => ({}))
      if (data.success) {
        window.location.reload()
        return
      }

      const message = Array.isArray(data.errors) ? data.errors.join("\n") : (data.error || "단계를 삭제할 수 없습니다.")
      alert(message)
    } catch (e) {
      console.error("Delete step failed:", e)
      alert("단계를 삭제할 수 없습니다. 잠시 후 다시 시도해주세요.")
    }
  }
}
